import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Form from 'antd/lib/form'
import Spin from 'antd/lib/spin'
import CheckboxCircleFillIcon from 'remixicon-react/CheckboxCircleFillIcon'
import Attachment2Icon from 'remixicon-react/Attachment2Icon'
import Button from '@UI/antd/Button/Button'
import UploadField from '@UI/Form/Fields/Upload'
import InputField from '@UI/Form/Fields/input'
import TextareaField from '@UI/Form/Fields/textarea'
import { LOCALSTORAGE_KEYS, COOKIE_KEYS, getCookie } from '@features/General'
import { rootActions } from '@app-store/slices'
import { RootState } from '@store/store'
import PreLoginLayout from '@components/Layouts/LayoutProvider/Layouts/PreLoginLayout'
import PhoneCountryList from '@UI/PhoneCountryList'
import { uploadMultiFileHelper } from '@utils/helpers/fileHelpers'
import styles from './index.scss'

interface SupportProps {
    onClose?: () => void
}

const Support = (props: SupportProps) => {
    const { onClose } = props
    const dispatch = useDispatch()
    const userInfo: any = JSON.parse(localStorage.getItem(LOCALSTORAGE_KEYS.USERINFO))
    const eventId = getCookie(COOKIE_KEYS.ALA_EVENT)
    const countries = useSelector((state: RootState) => state.countries.list.data)
    const authData = useSelector((state: RootState) => state.events.oneEventByDomain.data)
    const eventRecord = useSelector((state: RootState) => state.events.oneEvent.data)
    const { data: i18n } = useSelector((state: RootState) => state.localization.locals)
    const [showSuccessMsg, setShowSuccessMsg] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [fileList, setFileList] = useState([])
    const [form] = Form.useForm()
    useEffect(() => {
        const pagination = {
            hasTotal: true,
            limit: 1000
        }
        dispatch(rootActions.countries.list.onRequest({ pagination }))
    }, [])

    const sagaFn = () => {
        if (typeof onClose === 'function') {
            onClose()
        } else {
            setShowSuccessMsg(true)
        }
        setIsSubmitting(false)
        setFileList([])
        form.resetFields()
        form.setFieldsValue({ prefix: countries?.[0]?.callingCodes?.[0] || '' })
    }
    const submit = async values => {
        setIsSubmitting(true)
        const data = { ...values }
        if (data.attachment) {
            delete data.attachment
        }
        if (values.phone) {
            delete data.prefix
            data.phone = '+' + (values.prefix || '') + values.phone
        }
        if (!values.priority) {
            data.priority = 'LOW'
        }
        if (!values.event) {
            if (authData?.name) {
                data.event = authData?.name
            } else if (eventId && eventRecord) {
                data.event = eventRecord.name || ''
            }
        }
        const images = await uploadMultiFileHelper(
            fileList,
            eventId ? '/attachments/properties/upload-url' : 'attachments/properties/open-upload-url'
        )
        data.images = images.uploadResult.map(item => item.uploadURL.split('?')[0])
        dispatch(
            rootActions.support[userInfo ? 'loggedinUserSupport' : 'anonymousSupport'].onRequest({
                ...data,
                sagaCB: {
                    onSuccess: () => sagaFn(),
                    onError: () => sagaFn()
                }
            })
        )
    }

    useEffect(() => {
        form.setFieldsValue({ prefix: countries?.[0]?.callingCodes?.[0] || '' })
    }, [countries])

    return (
        <Spin spinning={isSubmitting}>
            <PreLoginLayout isModal={!!onClose}>
                {showSuccessMsg ? (
                    <div className={styles.success}>
                        <CheckboxCircleFillIcon size={90} />
                        <h3>Thank you for reaching out!</h3>
                        <p>
                            {`Your message is in the hands of our Aladdin Genies, and we'll be in touch within 24 hours.`}
                        </p>
                    </div>
                ) : (
                    <div className={styles.support}>
                        <h5 className={styles.modalData}>
                            {i18n?.General?.Need_help_feedback || 'Need Help or Have Feedback?'}
                        </h5>
                        <p>
                            {i18n?.General?.experts_help_answer_msg ||
                                'We’re here to listen! Whether you have a question, need support, or want to share your feedback, our experts are ready to assist you.'}
                        </p>
                        <h6>How can we help you today?</h6>
                        <Form onFinish={submit} form={form}>
                            {userInfo ? (
                                <>
                                    {!eventId && (
                                        <InputField
                                            name="event"
                                            label={i18n?.General?.Event_Name || 'Event Name'}
                                            required
                                            requiredMsg={
                                                i18n?.General?.Please_enter_event_name || 'Please enter event name'
                                            }
                                        />
                                    )}
                                </>
                            ) : (
                                <>
                                    <InputField
                                        name="name"
                                        label={i18n?.General?.['Full Name']}
                                        required
                                        requiredMsg={i18n?.General?.Please_enter_full_name || 'Please enter full name'}
                                    />
                                    <InputField
                                        name="company"
                                        label={i18n?.General?.Company_Name || 'Company Name'}
                                        required
                                        requiredMsg={
                                            i18n?.General?.Please_enter_company_name || 'Please enter company name'
                                        }
                                    />
                                    <InputField
                                        name="phone"
                                        addonBefore={<PhoneCountryList />}
                                        label={i18n?.General?.Enter_your_phone_number || 'Enter your phone number'}
                                        required
                                        requiredMsg={
                                            i18n?.General?.Please_enter_phone_number || 'Please enter phone number'
                                        }
                                    />
                                    <InputField
                                        label={i18n?.General?.Enter_your_email_address || 'Enter your email address'}
                                        name="email"
                                        required
                                        requiredMsg={i18n?.General?.Please_enter_email || 'Please enter email'}
                                    />
                                    {!authData?.name && (
                                        <InputField
                                            name="event"
                                            label={i18n?.General?.Event_Name || 'Event Name'}
                                            required
                                            requiredMsg={
                                                i18n?.General?.Please_enter_event_name || 'Please enter event name'
                                            }
                                        />
                                    )}
                                </>
                            )}
                            <TextareaField
                                label={i18n?.General?.Description || 'Description'}
                                placeholder={
                                    i18n?.General?.Description_support_msg ||
                                    'Please share your feedback or describe the issue you’re experiencing. Feel free to attach any files that might help us understand better.'
                                }
                                name="description"
                                required
                                requiredMsg={i18n?.General?.Please_enter_description || 'Please enter description'}
                            />
                            <UploadField
                                name="attachment"
                                fileList={fileList}
                                setFileList={fileList => setFileList(fileList)}
                                shape="rectangle"
                                multiple
                                icon={
                                    <div>
                                        {i18n?.General?.['Attache File']}
                                        <Attachment2Icon size={30} color="$primaryColor" />
                                    </div>
                                }
                            />
                            <Button className={styles.buttonTicket} typeButton="primary-solid" htmlType="submit">
                                {i18n?.General?.Send}
                            </Button>
                            <p className={styles.supportData}>
                                By clicking submit, you agree to our{' '}
                                <a target="_blank" href="https://aladdinb2b.com/privacy/" rel="noreferrer">
                                    Privacy Policy
                                </a>
                                {` and `}
                                <a target="_blank" href="https://aladdinb2b.com/terms-and-conditions/" rel="noreferrer">
                                    Terms of Use
                                </a>
                                , and consent to being contacted by AladdinB2B Support.
                            </p>
                        </Form>
                    </div>
                )}
            </PreLoginLayout>
        </Spin>
    )
}

export default Support
